import { CustomDimensionsOrParams } from '../../google-analytics/ga4Types';
import {
  ConfigRepositoryParameters,
  GenericConfigParameters,
} from '../../../configParametersRepository/types';
import { getQueryParameterFromUrl } from './utils';
import { CUSTOM_PARAMS_STORAGE_KEY } from '../../resources/constants';
import { CustomDimensions } from '../../types';
import { get } from '../../../utils/httpRequest';
import { createRuntimeGoogleState } from './state';
import { trackPage } from '../../thirdPartyTrackingAgent/pages';
import { createConfigParametersRepository } from '../../../configParametersRepository';
import { SITE_TYPE } from './constants';

export const createDefaultDimensions = (
  response: CustomDimensionsOrParams,
  genericParameters: Partial<GenericConfigParameters>,
  buyPathDimension: string
): CustomDimensionsOrParams => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();
  let dimensions: CustomDimensionsOrParams = {
    dimension7: genericParameters.dapiSessionId,
    dimension8: genericParameters.locale,
    dimension9: SITE_TYPE[genericParameters.flow],
    dimension50:
      getQueryParameterFromUrl('referral', window.location.href) || '',
    dimension62: document.referrer.substring(0, 1200),
    dimension73: buyPathDimension,
  };

  if (genericParameters.isNativeInterface) {
    const { device } = genericParameters;
    const brand = {
      ED: 'EDREAMS',
      GV: 'GOV', // legacy
      GO: 'GOV', // new
      OP: 'OPODO',
      TL: 'TRAVELLINK', // legacy
      TR: 'TRAVELLINK', // new
    }[genericParameters.brand];

    dimensions = {
      ...dimensions,
      dimension40: device + brand,
      dimension9: 'A_' + device.charAt(0),
      app_traffic: 'TRUE',
    };
  }

  const existingCustomDimensions = storageClient?.getTrackingStoredValue(
    CUSTOM_PARAMS_STORAGE_KEY
  ) as CustomDimensionsOrParams;

  return {
    ...existingCustomDimensions,
    ...response,
    ...dimensions,
  };
};

const parseWebsiteForMarketParam = (website: string) => {
  if (website?.length === 2) {
    return 'ED' + website;
  } else if (website?.length > 4) {
    return website.substring(0, 4);
  }
  return website;
};

export const updateCustomDimensions = (
  customDimensions: CustomDimensionsOrParams,
  genericParameters: Partial<GenericConfigParameters>
) => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();
  const defaultCustomDimensions = {
    ...customDimensions,
    market: parseWebsiteForMarketParam(genericParameters.website),
  };
  storageClient?.updateCustomParamsAndDimensions(defaultCustomDimensions);
};

export const trackPostponedPageviews = () => {
  const { getPagesQueue } = createRuntimeGoogleState();
  const pagesQueue = getPagesQueue();
  pagesQueue?.forEach((page) => trackPage(page.page, page.options));
};

export const setDefaultCustomDimensions = (
  genericParameters: Partial<GenericConfigParameters>,
  response: CustomDimensionsOrParams
) => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();
  const { setParameters } = createRuntimeGoogleState();
  const { buypath } = storageClient.getTrackingStoredValue(
    CUSTOM_PARAMS_STORAGE_KEY
  );
  const buyPathDimension = (buypath as string) || '';

  const customDimensions = createDefaultDimensions(
    response,
    genericParameters,
    buyPathDimension
  );
  updateCustomDimensions(customDimensions, genericParameters);

  setParameters({
    hasSetDefaultCustomDimensions: true,
  });
  trackPostponedPageviews();
};

export const getDefaultCustomDimensions = async (
  configParameters: ConfigRepositoryParameters
) => {
  const { endpoint } = configParameters.googleAnalytics;
  const { generic: genericParameters } = configParameters;
  const url = endpoint.defaultCustomDimensions;

  try {
    const response: CustomDimensions = await get(url);
    setDefaultCustomDimensions(genericParameters, response);
  } catch (_) {
    setDefaultCustomDimensions(genericParameters, {});
  }
};
